import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { calculateAmortization } from '../services/api';
import { EdFiLogo } from '../components/EdFiLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Calculator, ArrowLeft, Download, TrendingUp, DollarSign, Percent, Calendar } from 'lucide-react';

const CalculatorPage = () => {
  const [amount, setAmount] = useState(80000);
  const [termMonths, setTermMonths] = useState('12');
  const [scoring, setScoring] = useState('promedio');
  const [result, setResult] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const response = await calculateAmortization({
        amount,
        term_months: parseInt(termMonths),
        scoring
      });
      setResult(response.data);
    } catch (error) {
      console.error('Error calculating:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <EdFiLogo size="sm" linkTo="/" />
              <Link to="/" className="text-slate-600 hover:text-brand-blue transition-colors flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                Inicio
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/programs">
                <Button variant="outline" className="border-brand-blue text-brand-blue">
                  Ver Programas
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-brand-magenta hover:bg-brand-magenta-dark text-white">
                  Aplicar Ahora
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-brand-blue py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Calculator className="w-16 h-16 text-white mx-auto mb-4" />
          <h1 className="text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
            Calculadora de Crédito
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Simula tu financiamiento y conoce cuánto pagarías mensualmente
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Configura tu crédito</h2>
            
            <div className="space-y-6">
              <div>
                <Label className="text-slate-700 mb-2 block">Monto a financiar</Label>
                <div className="mb-4">
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(Math.max(20000, Math.min(100000, parseInt(e.target.value) || 20000)))}
                    className="text-xl font-bold"
                    data-testid="amount-input"
                  />
                </div>
                <Slider
                  value={[amount]}
                  onValueChange={(value) => setAmount(value[0])}
                  min={20000}
                  max={100000}
                  step={5000}
                  className="mb-2"
                />
                <div className="flex justify-between text-sm text-slate-500">
                  <span>$20,000</span>
                  <span>$100,000</span>
                </div>
              </div>

              <div>
                <Label className="text-slate-700 mb-2 block">Plazo</Label>
                <Select value={termMonths} onValueChange={setTermMonths}>
                  <SelectTrigger data-testid="term-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 meses</SelectItem>
                    <SelectItem value="6">6 meses</SelectItem>
                    <SelectItem value="9">9 meses</SelectItem>
                    <SelectItem value="12">12 meses</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-700 mb-2 block">Perfil crediticio</Label>
                <Select value={scoring} onValueChange={setScoring}>
                  <SelectTrigger data-testid="scoring-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bueno">Bueno (1.5% mensual)</SelectItem>
                    <SelectItem value="promedio">Promedio (4% mensual)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-2">
                  Tu tasa final dependerá de tu evaluación crediticia
                </p>
              </div>

              <Button
                className="w-full bg-brand-magenta hover:bg-brand-magenta-dark text-white py-6 text-lg"
                onClick={handleCalculate}
                disabled={loading}
                data-testid="calculate-button"
              >
                {loading ? 'Calculando...' : 'Calcular Crédito'}
              </Button>
            </div>
          </div>

          {/* Results */}
          <div>
            {result ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-8 animate-fade-up">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Tu estimación</h2>
                
                <div className="bg-brand-blue rounded-xl p-6 mb-6 text-white">
                  <p className="text-sm opacity-80 mb-1">Pago mensual</p>
                  <p className="text-4xl font-black" data-testid="result-monthly-payment">
                    {formatPrice(result.monthly_payment)}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                      <DollarSign className="w-4 h-4" />
                      <span className="text-sm">Monto</span>
                    </div>
                    <p className="text-xl font-bold text-slate-900">{formatPrice(result.amount)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">Plazo</span>
                    </div>
                    <p className="text-xl font-bold text-slate-900">{result.term_months} meses</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                      <Percent className="w-4 h-4" />
                      <span className="text-sm">Tasa mensual</span>
                    </div>
                    <p className="text-xl font-bold text-slate-900">{(result.interest_rate_monthly * 100).toFixed(1)}%</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm">Total a pagar</span>
                    </div>
                    <p className="text-xl font-bold text-slate-900">{formatPrice(result.total_payment)}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl mb-6">
                  <span className="text-slate-600">Intereses totales</span>
                  <span className="text-lg font-bold text-slate-900">{formatPrice(result.total_interest)}</span>
                </div>

                <Button
                  variant="outline"
                  className="w-full mb-4"
                  onClick={() => setShowSchedule(!showSchedule)}
                  data-testid="toggle-schedule"
                >
                  {showSchedule ? 'Ocultar' : 'Ver'} tabla de amortización
                </Button>

                <Link to="/programs">
                  <Button className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white">
                    Explorar Programas
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
                <Calculator className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  Calcula tu crédito
                </h3>
                <p className="text-slate-600">
                  Configura los parámetros y presiona calcular para ver tu estimación
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Amortization Schedule */}
        {result && showSchedule && (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 mt-8 animate-fade-up">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Tabla de Amortización</h2>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Descargar
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mes</TableHead>
                    <TableHead className="text-right">Saldo Inicial</TableHead>
                    <TableHead className="text-right">Pago</TableHead>
                    <TableHead className="text-right">Interés</TableHead>
                    <TableHead className="text-right">Capital</TableHead>
                    <TableHead className="text-right">Saldo Final</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.schedule.map((row) => (
                    <TableRow key={row.month}>
                      <TableCell className="font-medium">{row.month}</TableCell>
                      <TableCell className="text-right">{formatPrice(row.initial_balance)}</TableCell>
                      <TableCell className="text-right font-semibold">{formatPrice(row.payment)}</TableCell>
                      <TableCell className="text-right text-slate-600">{formatPrice(row.interest)}</TableCell>
                      <TableCell className="text-right text-slate-600">{formatPrice(row.principal)}</TableCell>
                      <TableCell className="text-right">{formatPrice(row.final_balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalculatorPage;
